/**
 * TableSliteControls - Slite/Notion-style table controls
 * - Row handle with dropdown on left AND right edges (on row hover)
 * - Column handle with dropdown on top AND bottom edges (on col hover)
 * - Table menu (delete table, toggle header) in top-left
 * - Column and row resize handled by ProseMirror plugins in RichTextEditor
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Plus, MoreHorizontal, Trash2, Rows3, Columns3, PanelTop } from 'lucide-react';
import type { Editor } from '@tiptap/core';
import styles from './TableSliteControls.module.scss';

const BUTTON_SIZE = 24;
const OFFSET = 4;
const WRAPPER_PADDING = 24; // matches $spacing-lg in tableWrapper

function getFirstRowInfo(editor: Editor): { pos: number; hidden: boolean } | null {
  const { state } = editor;
  const { from } = state.selection;
  const $pos = state.doc.resolve(from);
  for (let d = $pos.depth; d > 0; d--) {
    if ($pos.node(d).type.name === 'table') {
      const tableStart = $pos.start(d);
      const firstRow = state.doc.nodeAt(tableStart);
      if (!firstRow || firstRow.type.name !== 'tableRow') return null;
      return { pos: tableStart, hidden: firstRow.attrs.hidden === true };
    }
  }
  return null;
}


function setSelectionToRow(editor: Editor, table: HTMLTableElement, rowIndex: number): boolean {
  const rows = table.querySelectorAll('tr');
  const tr = rows[rowIndex];
  if (!tr) return false;
  const firstCell = tr.querySelector('td, th');
  if (!firstCell) return false;
  const pos = editor.view.posAtDOM(firstCell as Node, 0);
  if (pos === null || pos === undefined) return false;
  editor.chain().focus().setTextSelection(pos).run();
  return true;
}

function setSelectionToCol(editor: Editor, table: HTMLTableElement, colIndex: number): boolean {
  const firstRow = table.querySelector('tr');
  if (!firstRow) return false;
  const cell = firstRow.children[colIndex] as HTMLElement | undefined;
  if (!cell) return false;
  const pos = editor.view.posAtDOM(cell as Node, 0);
  if (pos === null || pos === undefined) return false;
  editor.chain().focus().setTextSelection(pos).run();
  return true;
}

export interface TableSliteControlsProps {
  editor: Editor | null;
}

export function TableSliteControls({ editor }: TableSliteControlsProps) {
  const [tableRect, setTableRect] = React.useState<DOMRect | null>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<number | null>(null);
  const [hoveredRowRect, setHoveredRowRect] = React.useState<DOMRect | null>(null);
  const [tableMenuOpen, setTableMenuOpen] = React.useState(false);
  const [rowLeftMenuOpen, setRowLeftMenuOpen] = React.useState(false);
  const [rowRightMenuOpen, setRowRightMenuOpen] = React.useState(false);
  const [hoveredColIndex, setHoveredColIndex] = React.useState<number | null>(null);
  const [hoveredColRect, setHoveredColRect] = React.useState<DOMRect | null>(null);
  const [colTopMenuOpen, setColTopMenuOpen] = React.useState(false);
  const [colBottomMenuOpen, setColBottomMenuOpen] = React.useState(false);
  const tableRef = React.useRef<HTMLTableElement | null>(null);
  const rafRef = React.useRef<number | null>(null);
  const tableMenuButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const rowHandleLeftButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const rowHandleRightButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const colHandleTopButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const colHandleBottomButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const hoveredRowIndexRef = React.useRef<number | null>(null);
  const hoveredColIndexRef = React.useRef<number | null>(null);

  const anyMenuOpen = tableMenuOpen || rowLeftMenuOpen || rowRightMenuOpen || colTopMenuOpen || colBottomMenuOpen;

  // Single document-level mousemove handles everything: table detection,
  // row hover, and column hover. Controls appear on table hover, not on click.
  React.useEffect(() => {
    if (!editor) return;

    const onDocMouseMove = (e: MouseEvent) => {
      if (anyMenuOpen) return;

      // Find if cursor is over any tableWrapper (or its table) inside the editor.
      const target = e.target as HTMLElement;
      const wrapperEl = target.closest?.('.tableWrapper');
      const inWrapper = wrapperEl && editor.view.dom.contains(wrapperEl);
      const tableEl = inWrapper ? wrapperEl.querySelector('table') : null;

      // Also keep controls visible when cursor is over the portal buttons.
      const overAnyBtn =
        tableMenuButtonRef.current?.contains(target) ||
        rowHandleLeftButtonRef.current?.contains(target) ||
        rowHandleRightButtonRef.current?.contains(target) ||
        colHandleTopButtonRef.current?.contains(target) ||
        colHandleBottomButtonRef.current?.contains(target);

      if (!inWrapper && !overAnyBtn) {
        // Cursor outside — hide everything unless a menu is open.
        if (tableRef.current) {
          tableRef.current = null;
          setTableRect(null);
          setHoveredRowIndex(null);
          setHoveredRowRect(null);
          setHoveredColIndex(null);
          setHoveredColRect(null);
        }
        return;
      }

      // Update tableRef / tableRect when we land on a (possibly new) table.
      const table = tableEl ?? tableRef.current;
      if (!table) return;
      if (tableRef.current !== table) {
        tableRef.current = table;
        setTableRect(table.getBoundingClientRect());
      }

      // --- Row hover: trigger on left OR right padding zone ---
      {
        const rows = table.querySelectorAll('tr');
        let rowFound = false;
        for (let i = 0; i < rows.length; i++) {
          const rect = rows[i]!.getBoundingClientRect();
          if (
            e.clientX >= rect.left - BUTTON_SIZE - OFFSET &&
            e.clientX <= rect.right + BUTTON_SIZE + OFFSET &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom
          ) {
            hoveredRowIndexRef.current = i;
            setHoveredRowIndex(i);
            setHoveredRowRect(rect);
            rowFound = true;
            break;
          }
        }
        if (!rowFound) {
          hoveredRowIndexRef.current = null;
          setHoveredRowIndex(null);
          setHoveredRowRect(null);
        }
      }

      // --- Column hover: trigger on top OR bottom padding zone ---
      {
        const firstRow = table.querySelector('tr');
        if (firstRow) {
          const tableBottom = table.getBoundingClientRect().bottom;
          const cells = Array.from(firstRow.children) as HTMLElement[];
          let colFound = false;
          for (let i = 0; i < cells.length; i++) {
            const rect = cells[i]!.getBoundingClientRect();
            if (
              e.clientX >= rect.left &&
              e.clientX <= rect.right &&
              e.clientY >= rect.top - BUTTON_SIZE - OFFSET &&
              e.clientY <= tableBottom + BUTTON_SIZE + OFFSET
            ) {
              hoveredColIndexRef.current = i;
              setHoveredColIndex(i);
              setHoveredColRect(rect);
              colFound = true;
              break;
            }
          }
          if (!colFound) {
            hoveredColIndexRef.current = null;
            setHoveredColIndex(null);
            setHoveredColRect(null);
          }
        }
      }
    };

    // Update all rects on scroll so buttons track the table position.
    const onScroll = () => {
      if (!tableRef.current) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const table = tableRef.current;
        if (!table) return;
        setTableRect(table.getBoundingClientRect());
        if (hoveredRowIndexRef.current !== null) {
          const row = table.querySelectorAll('tr')[hoveredRowIndexRef.current];
          if (row) setHoveredRowRect(row.getBoundingClientRect());
        }
        if (hoveredColIndexRef.current !== null) {
          const firstRow = table.querySelector('tr');
          const cell = firstRow?.children[hoveredColIndexRef.current] as HTMLElement | undefined;
          if (cell) setHoveredColRect(cell.getBoundingClientRect());
        }
        rafRef.current = null;
      });
    };

    document.addEventListener('mousemove', onDocMouseMove);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousemove', onDocMouseMove);
      window.removeEventListener('scroll', onScroll, true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [editor, anyMenuOpen]);

  const deleteTable = () => editor?.chain().focus().deleteTable().run();

  const firstRowInfo = editor ? getFirstRowInfo(editor) : null;

  const toggleHeaderVisibility = () => {
    if (!editor || !firstRowInfo) return;
    editor.chain().focus().command(({ tr }) => {
      const node = tr.doc.nodeAt(firstRowInfo.pos);
      if (!node) return false;
      tr.setNodeMarkup(firstRowInfo.pos, undefined, {
        ...node.attrs,
        hidden: !firstRowInfo.hidden,
      });
      return true;
    }).run();
  };

  if (!editor || !tableRect) return null;

  const tableMenu = (
    <DropdownMenu.Root open={tableMenuOpen} onOpenChange={setTableMenuOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          ref={tableMenuButtonRef}
          type="button"
          className={styles.tableSlite__btn}
          onMouseDown={(e) => e.preventDefault()}
          title="Menú de tabla"
          aria-label="Menú de tabla"
          style={{
            left: tableRect.left - WRAPPER_PADDING,
            top: tableRect.top - WRAPPER_PADDING,
          }}
        >
          <MoreHorizontal size={14} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={styles.tableSlite__dropdown}
          align="start"
          side="right"
          sideOffset={6}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenu.Item
            className={styles.tableSlite__dropdownItem}
            onSelect={toggleHeaderVisibility}
          >
            <PanelTop size={14} />
            <span>{firstRowInfo?.hidden ? 'Mostrar cabecera' : 'Ocultar cabecera'}</span>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className={styles.tableSlite__separator} />
          <DropdownMenu.Item
            className={styles.tableSlite__dropdownItem}
            onSelect={deleteTable}
          >
            <Trash2 size={14} />
            <span>Eliminar tabla</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  const rowMenuContent = (closeMenu: () => void) => (
    <>
      <DropdownMenu.Item
        className={styles.tableSlite__dropdownItem}
        onSelect={() => {
          if (tableRef.current && hoveredRowIndex !== null) {
            setSelectionToRow(editor, tableRef.current, hoveredRowIndex);
            editor.chain().focus().addRowBefore().run();
          }
          closeMenu();
        }}
      >
        <Plus size={14} />
        <span>Insertar fila arriba</span>
      </DropdownMenu.Item>
      <DropdownMenu.Item
        className={styles.tableSlite__dropdownItem}
        onSelect={() => {
          if (tableRef.current && hoveredRowIndex !== null) {
            setSelectionToRow(editor, tableRef.current, hoveredRowIndex);
            editor.chain().focus().addRowAfter().run();
          }
          closeMenu();
        }}
      >
        <Plus size={14} />
        <span>Insertar fila abajo</span>
      </DropdownMenu.Item>
      <DropdownMenu.Separator className={styles.tableSlite__separator} />
      <DropdownMenu.Item
        className={styles.tableSlite__dropdownItem}
        onSelect={() => {
          if (tableRef.current && hoveredRowIndex !== null) {
            setSelectionToRow(editor, tableRef.current, hoveredRowIndex);
            editor.chain().focus().deleteRow().run();
          }
          closeMenu();
        }}
      >
        <Trash2 size={14} />
        <span>Eliminar fila</span>
      </DropdownMenu.Item>
    </>
  );

  const colMenuContent = (closeMenu: () => void) => (
    <>
      <DropdownMenu.Item
        className={styles.tableSlite__dropdownItem}
        onSelect={() => {
          if (tableRef.current && hoveredColIndex !== null) {
            setSelectionToCol(editor, tableRef.current, hoveredColIndex);
            editor.chain().focus().addColumnBefore().run();
          }
          closeMenu();
        }}
      >
        <Plus size={14} />
        <span>Insertar columna antes</span>
      </DropdownMenu.Item>
      <DropdownMenu.Item
        className={styles.tableSlite__dropdownItem}
        onSelect={() => {
          if (tableRef.current && hoveredColIndex !== null) {
            setSelectionToCol(editor, tableRef.current, hoveredColIndex);
            editor.chain().focus().addColumnAfter().run();
          }
          closeMenu();
        }}
      >
        <Plus size={14} />
        <span>Insertar columna después</span>
      </DropdownMenu.Item>
      <DropdownMenu.Separator className={styles.tableSlite__separator} />
      <DropdownMenu.Item
        className={styles.tableSlite__dropdownItem}
        onSelect={() => {
          if (tableRef.current && hoveredColIndex !== null) {
            setSelectionToCol(editor, tableRef.current, hoveredColIndex);
            editor.chain().focus().deleteColumn().run();
          }
          closeMenu();
        }}
      >
        <Trash2 size={14} />
        <span>Eliminar columna</span>
      </DropdownMenu.Item>
    </>
  );

  const rowHandles =
    hoveredRowRect !== null && hoveredRowIndex !== null ? (
      <>
        {/* Left row handle */}
        <DropdownMenu.Root open={rowLeftMenuOpen} onOpenChange={setRowLeftMenuOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              ref={rowHandleLeftButtonRef}
              type="button"
              className={styles.tableSlite__rowHandle}
              onMouseDown={(e) => e.preventDefault()}
              title="Menú de fila"
              aria-label="Menú de fila"
              style={{
                left: tableRect.left - WRAPPER_PADDING,
                top: hoveredRowRect.top + hoveredRowRect.height / 2 - BUTTON_SIZE / 2,
              }}
            >
              <Rows3 size={14} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={styles.tableSlite__dropdown}
              align="start"
              side="right"
              sideOffset={6}
              onCloseAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={() => setHoveredRowIndex(null)}
            >
              {rowMenuContent(() => setRowLeftMenuOpen(false))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {/* Right row handle */}
        <DropdownMenu.Root open={rowRightMenuOpen} onOpenChange={setRowRightMenuOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              ref={rowHandleRightButtonRef}
              type="button"
              className={styles.tableSlite__rowHandle}
              onMouseDown={(e) => e.preventDefault()}
              title="Menú de fila"
              aria-label="Menú de fila"
              style={{
                left: tableRect.right,
                top: hoveredRowRect.top + hoveredRowRect.height / 2 - BUTTON_SIZE / 2,
              }}
            >
              <Rows3 size={14} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={styles.tableSlite__dropdown}
              align="start"
              side="right"
              sideOffset={6}
              onCloseAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={() => setHoveredRowIndex(null)}
            >
              {rowMenuContent(() => setRowRightMenuOpen(false))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </>
    ) : null;

  const colHandles =
    hoveredColRect !== null && hoveredColIndex !== null ? (
      <>
        {/* Top column handle */}
        <DropdownMenu.Root open={colTopMenuOpen} onOpenChange={setColTopMenuOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              ref={colHandleTopButtonRef}
              type="button"
              className={styles.tableSlite__rowHandle}
              onMouseDown={(e) => e.preventDefault()}
              title="Menú de columna"
              aria-label="Menú de columna"
              style={{
                left: hoveredColRect.left + hoveredColRect.width / 2 - BUTTON_SIZE / 2,
                top: tableRect.top - WRAPPER_PADDING,
              }}
            >
              <Columns3 size={14} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={styles.tableSlite__dropdown}
              align="center"
              side="bottom"
              sideOffset={6}
              onCloseAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={() => setHoveredColIndex(null)}
            >
              {colMenuContent(() => setColTopMenuOpen(false))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {/* Bottom column handle */}
        <DropdownMenu.Root open={colBottomMenuOpen} onOpenChange={setColBottomMenuOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              ref={colHandleBottomButtonRef}
              type="button"
              className={styles.tableSlite__rowHandle}
              onMouseDown={(e) => e.preventDefault()}
              title="Menú de columna"
              aria-label="Menú de columna"
              style={{
                left: hoveredColRect.left + hoveredColRect.width / 2 - BUTTON_SIZE / 2,
                top: tableRect.bottom,
              }}
            >
              <Columns3 size={14} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={styles.tableSlite__dropdown}
              align="center"
              side="bottom"
              sideOffset={6}
              onCloseAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={() => setHoveredColIndex(null)}
            >
              {colMenuContent(() => setColBottomMenuOpen(false))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </>
    ) : null;

  return createPortal(
    <div className={styles.tableSlite} style={{ zIndex: 9998 }}>
      {tableMenu}
      {rowHandles}
      {colHandles}
    </div>,
    document.body
  );
}
